import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { api, resolveImageUrl } from "../lib/api";

const BATCH_SIZE = 12;
const TARGET = 8;

export default function Quiz() {
  const [allImages, setAllImages] = useState([]);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const [selected, setSelected] = useState([]); // ordered image ids
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const sentinelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.listImages();
        if (!cancelled) setAllImages(data);
      } catch (e) {
        setError("Could not load the image library. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => allImages.slice(0, visibleCount), [allImages, visibleCount]);
  const hasMore = visibleCount < allImages.length;

  // Infinite scroll: load next batch as the sentinel approaches the viewport.
  // rootMargin loads ~1200px early so the reveal feels smooth, not jarring.
  const loadMore = useCallback(() => {
    setVisibleCount((c) => Math.min(c + BATCH_SIZE, allImages.length));
  }, [allImages.length]);

  useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "600px 0px 600px 0px", threshold: 0 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loadMore, visibleCount]);

  const toggle = (img) => {
    setSelected((prev) => {
      if (prev.includes(img.id)) return prev.filter((x) => x !== img.id);
      if (prev.length >= TARGET) return prev; // cap
      return [...prev, img.id];
    });
  };

  const canReveal = selected.length === TARGET;
  const progress = Math.min(selected.length / TARGET, 1);

  const reveal = async () => {
    if (!canReveal || submitting) return;
    setSubmitting(true);
    try {
      const result = await api.score(selected);
      // Cache in sessionStorage for the results page
      sessionStorage.setItem("dna_result", JSON.stringify(result));
      sessionStorage.setItem("dna_selected", JSON.stringify(selected));
      navigate("/results");
    } catch (e) {
      setError("Scoring failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-32 grain" data-testid="quiz-page">
      {/* Header */}
      <div className="container-editorial pt-8 pb-6 flex justify-between items-center">
        <a href="/" className="font-serif-display text-2xl md:text-3xl">
          Aesthos<span className="text-[color:var(--terracotta)]">.</span>
        </a>
        <div className="overline hidden sm:block">Select {TARGET} · Reveal your DNA</div>
      </div>
      <div className="hairline container-editorial" />

      {/* Intro */}
      <div className="container-editorial pt-10 md:pt-16 pb-10 md:pb-14 max-w-3xl">
        <p className="overline mb-4" data-testid="quiz-eyebrow">Step 01</p>
        <h1 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mb-5" data-testid="quiz-title">
          Choose <em className="italic text-[color:var(--terracotta)]">eight</em> rooms
          <br />
          you&rsquo;re drawn to.
        </h1>
        <p className="text-base md:text-lg text-[color:var(--ink-soft)] leading-relaxed">
          Don&rsquo;t overthink. Trust your first pull. We&rsquo;ll read the pattern.
        </p>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="container-editorial">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={`skeleton-${i}`} className="aspect-[4/5] bg-[color:var(--border)] animate-pulse" />
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="container-editorial text-[color:var(--ink-soft)]">{error}</div>
      ) : (
        <>
          <div className="container-editorial">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="quiz-grid">
              {visible.map((img, idx) => {
                const selIdx = selected.indexOf(img.id);
                const isSel = selIdx >= 0;
                const atMax = !isSel && selected.length >= TARGET;
                return (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => toggle(img)}
                    className={`tile ${isSel ? "tile-selected" : ""} ${atMax ? "opacity-40" : ""}`}
                    style={{ animationDelay: `${(idx % 12) * 0.03}s` }}
                    data-testid="quiz-image-tile"
                    aria-pressed={isSel}
                    aria-label={`Option ${idx + 1}`}
                    disabled={atMax}
                  >
                    <img
                      src={resolveImageUrl(img)}
                      alt={`Option ${idx + 1}`}
                      loading="lazy"
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                      onError={(e) => {
                        e.target.style.background = "#eae6dc";
                        e.target.style.opacity = "0";
                      }}
                    />
                    {isSel && (
                      <span className="tile-badge" aria-hidden data-testid="tile-selected-badge">
                        <Check size={16} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {hasMore && (
              <div
                ref={sentinelRef}
                aria-hidden="true"
                className="h-16 mt-8"
                data-testid="quiz-scroll-sentinel"
              />
            )}
          </div>
        </>
      )}

      {/* Sticky counter */}
      <div className="counter-bar" style={{ transform: selected.length ? "translateY(0)" : "translateY(0)" }}>
        <div className="container-editorial py-4 md:py-5 flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="flex items-baseline justify-between mb-2">
              <span className="overline" data-testid="selection-counter">
                <span className="font-serif-display text-2xl md:text-3xl not-italic text-[color:var(--ink)] mr-2">
                  {selected.length}
                </span>
                / {TARGET} selected
              </span>
              {selected.length > 0 && selected.length < TARGET && (
                <span className="overline hidden md:inline">Keep going</span>
              )}
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${progress * 100}%` }} />
            </div>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={reveal}
            disabled={!canReveal || submitting}
            data-testid="reveal-style-button"
          >
            {submitting ? "Reading…" : "Reveal my style"}
            <span aria-hidden>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}
